import * as React from "react"
import { Check, ChevronsUpDown, Loader2 } from "lucide-react"

import { cn } from "../../../lib/utils"
import { Button } from "../button/button"
import { Icon } from "../icon/icon"
import type { IconName } from "../icon/icon-registry"
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover"
import { Skeleton } from "../skeleton/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../tooltip/tooltip"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../command/command"

export interface ComboboxOptionGroup<T> {
  /** Group heading (omit for ungrouped list). */
  heading?: string
  items: T[]
}

export interface SearchableComboboxProps<T> {
  /** Flat list of items — use `groups` for sectioned results (e.g. Recent + All). */
  items?: T[]
  /** Optional grouped items; takes precedence over `items`. */
  groups?: ComboboxOptionGroup<T>[]
  /** Unique, stable key for each item (used by React + cmdk value). */
  getKey: (item: T) => string
  /** Primary searchable/displayed label. */
  getLabel: (item: T) => string
  /**
   * Text the search query matches against — defaults to `getLabel`.
   * Use it to make a secondary identifier findable while the row still shows the
   * label, e.g. typing a country code to reach a country by name.
   */
  getSearchText?: (item: T) => string
  /** Optional richer row — falls back to `getLabel`. Receives selection + pending + disabled state. */
  renderItem?: (item: T, state: { isSelected: boolean; isSelecting: boolean; isDisabled: boolean }) => React.ReactNode
  /** Currently selected item's key. */
  value?: string | null
  /** Called when user picks an item. */
  onSelect: (item: T) => void
  /** Trigger button label when nothing is selected. */
  placeholder?: string
  /** Search input placeholder. */
  searchPlaceholder?: string
  /** Empty-state message when filter yields zero results. */
  emptyMessage?: string
  /** Loading flag — shows skeleton rows inside the popover. */
  loading?: boolean
  /** Error message — shows error state inside the popover (overrides loading/empty). */
  error?: string | null
  /** Disable the trigger. */
  disabled?: boolean
  /** Icon shown at the start of the trigger label. */
  triggerIcon?: IconName
  /** Accessible label for the trigger. Falls back to placeholder. */
  ariaLabel?: string
  /** Class applied to the trigger button. */
  triggerClassName?: string
  /** Class applied to the popover content. */
  contentClassName?: string
  /** Extra props spread onto the popover content (e.g. hover handlers, `onOpenAutoFocus`). */
  contentProps?: React.ComponentProps<typeof PopoverContent>
  /** Popover alignment. */
  align?: "start" | "center" | "end"
  /** Preferred side for popover. */
  side?: "top" | "right" | "bottom" | "left"
  /** Override the default trigger — receives the currently selected item (if any). */
  renderTrigger?: (state: {
    selected: T | null
    open: boolean
  }) => React.ReactNode
  /** Controlled open state. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Class applied to each option item. */
  itemClassName?: string
  /** Per-item class override (merged with `itemClassName`). */
  getItemClassName?: (
    item: T,
    state: { isSelected: boolean; isSelecting: boolean },
  ) => string | undefined
  /** Per-item inline styles (e.g. dynamic accent borders). */
  getItemStyle?: (
    item: T,
    state: { isSelected: boolean; isSelecting: boolean },
  ) => React.CSSProperties | undefined
  /** Key of the item currently being selected — shows a spinner on that row. */
  selectingKey?: string | null
  /** Close the popover after selection. Set false when selection triggers async work. */
  closeOnSelect?: boolean
  /** Mark individual items as non-selectable. Disabled items are visually muted and skipped by keyboard nav. */
  getDisabled?: (item: T) => boolean
  /** Optional content pinned below the list (e.g. a "Select all" action). Rendered inside the popover, outside the scroll area. */
  footer?: React.ReactNode
  /** Data-slot / test id. */
  "data-testid"?: string
}

/**
 * Accessible, generic searchable combobox built on `cmdk` + Radix `Popover`.
 *
 * Handles the full state matrix: **idle, loading, error, empty, filtered, selected**.
 * Fully keyboard-navigable (↑/↓, Enter, Esc), dark/light aware via design tokens, and
 * works on touch devices. Prefer this over raw `Command` when you need a trigger-bound
 * dropdown picker.
 */
export function SearchableCombobox<T>({
  items,
  groups,
  getKey,
  getLabel,
  getSearchText,
  renderItem,
  value,
  onSelect,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No results found.",
  loading = false,
  error = null,
  disabled = false,
  triggerIcon,
  ariaLabel,
  triggerClassName,
  contentClassName,
  contentProps,
  align = "start",
  side = "bottom",
  renderTrigger,
  open: openProp,
  onOpenChange,
  itemClassName,
  getItemClassName,
  getItemStyle,
  selectingKey = null,
  closeOnSelect = true,
  getDisabled,
  footer,
  "data-testid": testId,
}: SearchableComboboxProps<T>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const open = openProp ?? uncontrolledOpen
  const setOpen = React.useCallback(
    (next: boolean) => {
      onOpenChange?.(next)
      if (openProp === undefined) setUncontrolledOpen(next)
    },
    [onOpenChange, openProp],
  )

  const listRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (!open || !value) return
    const id = requestAnimationFrame(() => {
      const el = listRef.current?.querySelector(`[data-value="${value}"]`)
      el?.scrollIntoView({ block: 'nearest' })
    })
    return () => cancelAnimationFrame(id)
  }, [open, value])

  const resolvedGroups: ComboboxOptionGroup<T>[] = React.useMemo(() => {
    if (groups && groups.length > 0) return groups
    return [{ items: items ?? [] }]
  }, [groups, items])

  const flatItems = React.useMemo(
    () => resolvedGroups.flatMap((g) => g.items),
    [resolvedGroups],
  )
  const itemsByKey = React.useMemo(() => {
    const map = new Map<string, T>()
    for (const item of flatItems) map.set(getKey(item), item)
    return map
  }, [flatItems, getKey])

  const selected = React.useMemo(
    () => (value ? itemsByKey.get(value) ?? null : null),
    [itemsByKey, value],
  )

  // Filter on label text — cmdk defaults to matching rendered children, which
  // breaks as soon as a consumer supplies `renderItem`.
  const filter = React.useCallback(
    (valueKey: string, search: string) => {
      const item = itemsByKey.get(valueKey)
      if (!item) return 0
      const haystack = getSearchText ? getSearchText(item) : getLabel(item)
      return haystack.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
    },
    [getLabel, getSearchText, itemsByKey],
  )

  const handleSelect = (item: T) => {
    if (selectingKey) return
    onSelect(item)
    if (closeOnSelect) setOpen(false)
  }

  const trigger = renderTrigger ? (
    <span>{renderTrigger({ selected, open })}</span>
  ) : (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      aria-label={ariaLabel ?? placeholder}
      disabled={disabled}
      className={cn(
        "w-full justify-between gap-2 font-normal",
        !selected && "text-muted-foreground",
        triggerClassName,
      )}
      data-testid={testId}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {triggerIcon ? (
          <Icon name={triggerIcon} size="sm" color="muted" />
        ) : null}
        {selected ? (
          <TruncatedLabel text={getLabel(selected)} side="bottom" />
        ) : (
          <span className="truncate">{placeholder}</span>
        )}
      </span>
      <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
    </Button>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        {...contentProps}
        align={align}
        side={side}
        sideOffset={contentProps?.sideOffset ?? 4}
        className={cn(
          "flex w-(--radix-popover-trigger-width) max-h-[min(var(--radix-popover-content-available-height),24rem)] flex-col overflow-hidden bg-muted p-0",
          contentClassName,
          contentProps?.className,
        )}
      >
        <Command
          className="flex min-h-0 flex-1 flex-col overflow-hidden bg-muted"
          filter={filter}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList
            ref={listRef}
            className="max-h-none min-h-0 flex-1 overflow-y-auto overscroll-contain"
          >
            {error ? (
              <ComboboxErrorState message={error} />
            ) : loading ? (
              <ComboboxLoadingState />
            ) : (
              <>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                {resolvedGroups.map((group, groupIndex) => {
                  if (group.items.length === 0) return null
                  return (
                    <React.Fragment key={group.heading ?? `group-${groupIndex}`}>
                      {groupIndex > 0 ? <CommandSeparator /> : null}
                      <CommandGroup heading={group.heading}>
                        {group.items.map((item) => {
                          const key = getKey(item)
                          const isSelected = key === value
                          const isSelecting = key === selectingKey
                          const isDisabled = (getDisabled ? getDisabled(item) : false) || !!selectingKey
                          return (
                            <CommandItem
                              key={key}
                              value={key}
                              onSelect={() => handleSelect(item)}
                              aria-selected={isSelected}
                              aria-busy={isSelecting}
                              disabled={isDisabled}
                              className={cn(
                                itemClassName,
                                getItemClassName?.(item, { isSelected, isSelecting }),
                              )}
                              style={getItemStyle?.(item, { isSelected, isSelecting })}
                            >
                              {renderItem ? (
                                renderItem(item, { isSelected, isSelecting, isDisabled })
                              ) : (
                                <TruncatedLabel
                                  text={getLabel(item)}
                                  className="min-w-0 flex-1"
                                  side="right"
                                />
                              )}
                              {isSelecting ? (
                                <Loader2
                                  aria-hidden
                                  className="ml-auto size-4 shrink-0 animate-spin opacity-70"
                                />
                              ) : (
                                <Check
                                  aria-hidden
                                  className={cn(
                                    "ml-auto size-4 shrink-0",
                                    isSelected ? "opacity-100" : "opacity-0",
                                  )}
                                />
                              )}
                            </CommandItem>
                          )
                        })}
                      </CommandGroup>
                    </React.Fragment>
                  )
                })}
              </>
            )}
          </CommandList>
        </Command>
        {footer ? (
          <div data-slot="combobox-footer" className="shrink-0 border-t border-border/70">
            {footer}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

function ComboboxLoadingState() {
  return (
    <div
      data-slot="combobox-loading"
      role="status"
      aria-live="polite"
      aria-label="Loading"
      className="space-y-2 p-2"
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  )
}

/**
 * Renders `text` in a `truncate` span wrapped in a Radix `Tooltip` that shows
 * the full label on hover / focus after a short delay. Works reliably inside
 * `cmdk` where native `title` tooltips are reset by rapid `data-selected`
 * toggles on pointer-move.
 */
function TruncatedLabel({
  text,
  className,
  side = "bottom",
}: {
  text: string
  className?: string
  side?: "top" | "right" | "bottom" | "left"
}) {
  return (
    <TooltipProvider delayDuration={250} skipDelayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn("block truncate", className)}>{text}</span>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align="start"
          className="max-w-xs break-words"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function ComboboxErrorState({ message }: { message: string }) {
  return (
    <div
      data-slot="combobox-error"
      role="alert"
      className="flex flex-col items-center gap-2 px-4 py-6 text-center text-sm"
    >
      <Icon name="alert-circle" size="md" color="destructive" />
      <p className="text-destructive">{message}</p>
    </div>
  )
}
